import { useCallback, useEffect, useRef, useState } from "react";

import { listOpenIncidentsQueryControllerList } from "@/generated/log-types/log-types";
import type { OpenIncidentResponseDto } from "@/generated/model";
import { offlineDb, type OfflineOpenIncident } from "@/lib/offline/db";
import { syncService } from "@/lib/offline/syncService";

interface UseOfflineOpenIncidentsResult {
  items: OpenIncidentResponseDto[];
  isLoading: boolean;
  isOffline: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Offline-first hook for the manager dashboard's "open incidents" rail
 * (NAU-219d). Mirrors the pattern in `use-offline-follow-up-actions`:
 *
 *   1. Immediately return cached rows from `offlineDb.openIncidents` (if any).
 *   2. If online, fetch fresh data from `/v1/nurseries/:id/out-of-range-incidents/open`
 *      and replace the cache.
 *   3. Filter out any incident with a *pending* local sign-off — the manager
 *      already saw it disappear from the UI; we don't want it to flicker back
 *      while a slow sync round-trip is in-flight.
 *   4. Subscribe to the sync service so the rail refreshes once a queued
 *      sign-off lands on the server.
 */
export function useOfflineOpenIncidents(
  nurseryId: string | null,
): UseOfflineOpenIncidentsResult {
  const [items, setItems] = useState<OpenIncidentResponseDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const initializedRef = useRef(false);
  const currentNurseryIdRef = useRef(nurseryId);

  const filterOutPendingSignoffs = useCallback(
    async (
      raw: OpenIncidentResponseDto[],
    ): Promise<OpenIncidentResponseDto[]> => {
      if (raw.length === 0) return raw;
      const ids = raw.map((i) => i.id);
      // Only "pending" hides a row — an "error" sign-off was rejected by the
      // server (sign-off lock), so the incident must stay visible on the rail.
      const pending = await offlineDb.incidentSignoffs
        .filter((s) => ids.includes(s.id) && s.syncState === "pending")
        .toArray();
      if (pending.length === 0) return raw;
      const pendingIds = new Set(pending.map((p) => p.id));
      return raw.filter((i) => !pendingIds.has(i.id));
    },
    [],
  );

  const loadFromCache = useCallback(async (): Promise<
    OpenIncidentResponseDto[]
  > => {
    if (!nurseryId) return [];
    const cached = await offlineDb.openIncidents
      .where("nurseryId")
      .equals(nurseryId)
      .toArray();
    // Newest first, matches the server ordering.
    return cached
      .sort(
        (a, b) =>
          new Date(b.openedAt).getTime() - new Date(a.openedAt).getTime(),
      )
      .map(cacheRowToDto);
  }, [nurseryId]);

  const saveToCache = useCallback(
    async (data: OpenIncidentResponseDto[]) => {
      if (!nurseryId) return;
      // Replace the per-nursery slice: drop any cached rows that aren't in
      // the fresh response (resolved, deleted, or the user lost access),
      // and upsert the rest.
      const fresh = new Set(data.map((i) => i.id));
      const stale = await offlineDb.openIncidents
        .where("nurseryId")
        .equals(nurseryId)
        .toArray();
      const toDelete = stale.filter((s) => !fresh.has(s.id)).map((s) => s.id);
      if (toDelete.length > 0) {
        await offlineDb.openIncidents.bulkDelete(toDelete);
      }
      const cachedAt = Date.now();
      await offlineDb.openIncidents.bulkPut(
        data.map<OfflineOpenIncident>((i) => ({
          id: i.id,
          nurseryId,
          coldStorageId: i.coldStorageId,
          coldStorageName: i.coldStorageName,
          openedAt: i.openedAt,
          firstReading: {
            id: i.firstReading.id,
            temperature: i.firstReading.temperature,
            thresholdMinAtLog: i.firstReading.thresholdMinAtLog ?? null,
            thresholdMaxAtLog: i.firstReading.thresholdMaxAtLog ?? null,
            recordedAt: i.firstReading.recordedAt,
            notes: i.firstReading.notes ?? null,
          },
          canSignOff: i.canSignOff,
          cachedAt,
        })),
      );
    },
    [nurseryId],
  );

  const fetchFromServer = useCallback(async (): Promise<
    OpenIncidentResponseDto[]
  > => {
    if (!nurseryId) return [];
    const response = await listOpenIncidentsQueryControllerList(nurseryId);
    return (response?.data as OpenIncidentResponseDto[]) || [];
  }, [nurseryId]);

  const refetch = useCallback(async () => {
    if (!nurseryId || !navigator.onLine) return;
    try {
      const fresh = await fetchFromServer();
      await saveToCache(fresh);
      const filtered = await filterOutPendingSignoffs(fresh);
      setItems(filtered);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to fetch"));
    }
  }, [nurseryId, fetchFromServer, saveToCache, filterOutPendingSignoffs]);

  // Initial load
  useEffect(() => {
    if (currentNurseryIdRef.current !== nurseryId) {
      currentNurseryIdRef.current = nurseryId;
      initializedRef.current = false;
    }
    if (initializedRef.current) return;
    let mounted = true;

    async function initialize() {
      if (!nurseryId) {
        setItems([]);
        setIsLoading(false);
        return;
      }
      const offline = !navigator.onLine;
      setIsOffline(offline);

      const cached = await loadFromCache();
      if (!mounted) return;
      const filteredCached = await filterOutPendingSignoffs(cached);
      if (!mounted) return;
      setItems(filteredCached);
      setIsLoading(false);

      if (!offline) {
        try {
          const fresh = await fetchFromServer();
          if (!mounted) return;
          await saveToCache(fresh);
          const filtered = await filterOutPendingSignoffs(fresh);
          if (!mounted) return;
          setItems(filtered);
          setError(null);
        } catch (err) {
          if (mounted && cached.length === 0) {
            setError(err instanceof Error ? err : new Error("Failed to fetch"));
          }
        }
      }

      if (mounted) initializedRef.current = true;
    }

    initialize();
    return () => {
      mounted = false;
    };
  }, [
    nurseryId,
    loadFromCache,
    fetchFromServer,
    saveToCache,
    filterOutPendingSignoffs,
  ]);

  // Online/offline listeners
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      refetch();
    };
    const handleOffline = () => setIsOffline(true);
    setIsOffline(!navigator.onLine);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [refetch]);

  // Sync subscription — refetch when a queued sign-off lands
  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  const previousPendingCountRef = useRef(0);
  useEffect(() => {
    const unsubscribe = syncService.subscribe((status) => {
      const prev = previousPendingCountRef.current;
      if (prev > 0 && status.pendingCount === 0) {
        refetchRef.current();
      }
      previousPendingCountRef.current = status.pendingCount;
    });
    return () => {
      unsubscribe();
    };
  }, []);

  // Refetch when the window regains focus or the tab becomes visible again —
  // readings are typically added on another screen/device before the manager
  // comes back to the dashboard, and the rail must not show stale state.
  useEffect(() => {
    const handleFocus = () => {
      refetchRef.current();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refetchRef.current();
      }
    };
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return { items, isLoading, isOffline, error, refetch };
}

function cacheRowToDto(row: OfflineOpenIncident): OpenIncidentResponseDto {
  return {
    id: row.id,
    coldStorageId: row.coldStorageId,
    coldStorageName: row.coldStorageName,
    openedAt: row.openedAt,
    firstReading: {
      id: row.firstReading.id,
      temperature: row.firstReading.temperature,
      thresholdMinAtLog: row.firstReading.thresholdMinAtLog,
      thresholdMaxAtLog: row.firstReading.thresholdMaxAtLog,
      recordedAt: row.firstReading.recordedAt,
      notes: row.firstReading.notes,
    },
    canSignOff: row.canSignOff ?? false,
  };
}
